import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MediaAttachButton } from "../media/MediaAttachButton";
import {
  ArrowLeft, Save, Eye, RefreshCw, Loader2, ShieldCheck, FileWarning,
  HelpCircle, BookOpen, Lock, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  efAdminDossierDetail, efAdminDossierSectionPatch, efAdminEntityPatch,
} from "../../lib/api";

/**
 * AdminDossierEditor — section-by-section editor for a place / property dossier.
 * Section statuses: draft → review → approved. Approval requires at least one
 * source UNLESS the section's evidence_class is `open_question` or `context_only`.
 *
 * The whole dossier only becomes publicly reachable when:
 *   1. At least one section is `approved`, AND
 *   2. The parent entity is flipped to `public=true`.
 * Both gates are enforced server-side; the editor exposes them in the entity
 * footer panel so it is a single, deliberate action.
 */

const EVIDENCE_CLASS_META = {
  verified_fact:        { label: "VERIFIED FACT",        color: "#5DD4B3", icon: ShieldCheck },
  reported_allegation:  { label: "REPORTED ALLEGATION",  color: "#E0A526", icon: FileWarning },
  open_question:        { label: "OPEN QUESTION",        color: "#9FA8D4", icon: HelpCircle },
  context_only:         { label: "CONTEXT ONLY",         color: "#A39E93", icon: BookOpen },
};

const STATUS_META = {
  draft:    { label: "DRAFT",    color: "#A39E93" },
  review:   { label: "REVIEW",   color: "#E0A526" },
  approved: { label: "APPROVED", color: "#5DD4B3" },
};

export const AdminDossierEditor = ({ token, userToken = null }) => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await efAdminDossierDetail(token, slug, userToken);
      setData(r);
    } catch (e) {
      toast.error(`Failed to load dossier: ${e?.response?.data?.detail || e.message}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [slug, token, userToken]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="p-10 font-mono text-[12px] text-[#A39E93] flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> loading dossier {slug}…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-10">
        <Link to="/admin/epstein" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
          ← back to Epstein admin
        </Link>
      </div>
    );
  }

  const { entity, sections, sources } = data;
  const sectionsCount = sections.length;
  const approvedCount = sections.filter((s) => s.section_status === "approved").length;

  return (
    <div className="p-4 sm:p-6 max-w-[1280px] mx-auto" data-testid="admin-dossier-editor">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/admin/epstein"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20]"
          >
            <ArrowLeft size={12} /> Epstein admin
          </Link>
          <span className="text-[#454038]">·</span>
          <h1 className="font-display text-2xl text-[#E4DFD3] leading-tight truncate">
            {entity.name}
          </h1>
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#7a7062] truncate">
            {entity.slug}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/epstein-files/places/${entity.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] border border-[#454038] hover:border-[#D92D20] px-2 py-1.5"
          >
            <Eye size={11} /> Public preview
          </a>
          <button
            onClick={reload}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#E4DFD3] border border-[#454038] hover:border-[#D92D20] px-2 py-1.5"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Entity-level row */}
      <EntityStatePanel
        entity={entity}
        approvedCount={approvedCount}
        sectionsCount={sectionsCount}
        token={token}
        userToken={userToken}
        onChange={reload}
      />

      <div className="mt-3">
        <MediaAttachButton token={token} userToken={userToken}
                           targetType="dossier" targetId={slug} recordName={entity.name} />
      </div>

      {/* Two-pane: sidebar + editor */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <aside className="border border-[#2D2A26] bg-[#0a0907]">
          <div className="px-3 py-2 border-b border-[#2D2A26] font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20]">
            // SECTIONS
          </div>
          <ul>
            {sections.map((s, i) => (
              <li key={i}>
                <button
                  onClick={() => setActiveIdx(i)}
                  data-testid={`admin-dossier-section-tab-${i}`}
                  className={
                    "w-full text-left px-3 py-2 border-l-2 transition-colors " +
                    (activeIdx === i
                      ? "border-[#D92D20] bg-[#D92D20]/[0.08]"
                      : "border-transparent hover:bg-white/[0.02]")
                  }
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3] truncate">
                    {String(i + 1).padStart(2, "0")} · {s.heading}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <StatusPill status={s.section_status} />
                    <ClassPill ec={s.evidence_class} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="border border-[#2D2A26] bg-[#0a0907] min-h-[400px]">
          {sections[activeIdx] && (
            <SectionEditor
              key={`${slug}-${activeIdx}`}
              slug={slug}
              idx={activeIdx}
              section={sections[activeIdx]}
              allSources={sources}
              token={token}
              userToken={userToken}
              onSaved={reload}
            />
          )}
        </main>
      </div>
    </div>
  );
};

// =============================================================================
// Entity state panel — flip public + lifecycle
// =============================================================================
const EntityStatePanel = ({ entity, approvedCount, sectionsCount, token, userToken, onChange }) => {
  const [busy, setBusy] = useState(false);

  const flipPublic = async () => {
    if (entity.public) {
      // Going back to private — always allowed.
      if (!window.confirm("Make this dossier private again? It will be removed from the public archive.")) return;
    } else {
      if (approvedCount === 0) {
        toast.error("Cannot publish — at least one section must be approved first.");
        return;
      }
      if (!window.confirm(`Publish ${entity.name} dossier? ${approvedCount} approved section(s) will become public.`)) return;
    }
    setBusy(true);
    try {
      await efAdminEntityPatch(token, entity.id, { public: !entity.public }, userToken);
      toast.success(entity.public ? "Dossier returned to private." : "Dossier published.");
      await onChange();
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#2D2A26] bg-[#0a0907] p-3 sm:p-4 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062]">// state</span>
        <span className={
          "font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-0.5 border " +
          (entity.public
            ? "text-[#5DD4B3] border-[#5DD4B3]/40 bg-[#5DD4B3]/[0.06]"
            : "text-[#E0A526] border-[#E0A526]/40 bg-[#E0A526]/[0.06]")
        }>
          {entity.public ? "PUBLIC" : "PRIVATE"}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] px-2 py-0.5 border border-[#454038]">
          lifecycle · {entity.lifecycle_status || "—"}
        </span>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062]">// sections</span>
        <span className="font-mono text-[11px] text-[#E4DFD3]">
          {approvedCount} approved
          <span className="text-[#5a544a]"> / {sectionsCount} total</span>
        </span>
      </div>

      <div className="flex-1" />

      <button
        onClick={flipPublic}
        disabled={busy || (!entity.public && approvedCount === 0)}
        data-testid="admin-dossier-flip-public"
        className={
          "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.35em] px-3 py-2 border " +
          (entity.public
            ? "border-[#E0A526] text-[#E0A526] hover:bg-[#E0A526]/10"
            : approvedCount > 0
              ? "border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#5DD4B3]/10"
              : "border-[#454038] text-[#5a544a] cursor-not-allowed")
        }
      >
        {entity.public ? <><Lock size={11} /> Make private</> : <><CheckCircle2 size={11} /> Publish dossier</>}
      </button>
    </div>
  );
};

// =============================================================================
// Section editor
// =============================================================================
const SectionEditor = ({ slug, idx, section, allSources, token, userToken, onSaved }) => {
  const [heading, setHeading] = useState(section.heading || "");
  const [body, setBody] = useState(section.body_md || "");
  const [evidenceClass, setEvidenceClass] = useState(section.evidence_class || "context_only");
  const [chosenSources, setChosenSources] = useState(section.sources || []);
  const [saving, setSaving] = useState(false);

  // Mark dirty so the Save button changes state
  const dirty = useMemo(() => (
    heading !== (section.heading || "")
    || body !== (section.body_md || "")
    || evidenceClass !== (section.evidence_class || "context_only")
    || JSON.stringify([...chosenSources].sort()) !== JSON.stringify([...(section.sources || [])].sort())
  ), [heading, body, evidenceClass, chosenSources, section]);

  // Editorial guard — block source assignment that conflicts with class
  const propertyContextOnlyIds = useMemo(
    () => new Set((allSources || []).filter((s) => s.tag === "property_context_only").map((s) => s.id)),
    [allSources]
  );
  const violatesAllegationRule = (
    evidenceClass === "reported_allegation"
    && chosenSources.some((id) => propertyContextOnlyIds.has(id))
  );

  const saveSection = async (newStatus = null) => {
    if (violatesAllegationRule && newStatus !== null) {
      toast.error("Cannot save: a `property_context_only` source is attached to a `reported_allegation` section.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        heading,
        body_md: body,
        evidence_class: evidenceClass,
        sources: chosenSources,
      };
      if (newStatus) payload.section_status = newStatus;
      await efAdminDossierSectionPatch(token, slug, idx, payload, userToken);
      toast.success(`Section saved${newStatus ? ` · status → ${newStatus}` : ""}`);
      await onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleSource = (sid) => {
    setChosenSources((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);
  };

  return (
    <div className="p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20]">
          // SECTION {String(idx + 1).padStart(2, "0")}
        </span>
        <StatusPill status={section.section_status} />
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
          updated · {section.updated_at?.slice(0, 19).replace("T", " ") || "—"}
        </span>
      </div>

      {/* Heading */}
      <label className="block font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062] mb-1">
        Heading
      </label>
      <input
        value={heading}
        onChange={(e) => setHeading(e.target.value)}
        data-testid="admin-dossier-heading-input"
        className="w-full bg-black border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3] mb-3"
      />

      {/* Evidence class */}
      <label className="block font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062] mb-1.5">
        Evidence class
      </label>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {Object.entries(EVIDENCE_CLASS_META).map(([k, m]) => {
          const Icon = m.icon;
          const active = evidenceClass === k;
          return (
            <button
              key={k}
              onClick={() => setEvidenceClass(k)}
              data-testid={`admin-dossier-evidence-class-${k}`}
              className={
                "inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.3em] px-2 py-1 border transition-colors " +
                (active ? "" : "border-[#454038] text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20]/60")
              }
              style={active ? { color: m.color, borderColor: `${m.color}aa`, background: `${m.color}10` } : {}}
            >
              <Icon size={10} /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <label className="block font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062] mb-1">
        Body (plain text / markdown-ish)
      </label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={12}
        data-testid="admin-dossier-body-textarea"
        placeholder="Write the section body here. Keep verified facts, reported allegations, and open questions clearly separated."
        className="w-full bg-black border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3] leading-relaxed mb-3 whitespace-pre-wrap"
      />

      {/* Sources picker */}
      <label className="block font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062] mb-1.5">
        Attached sources ({chosenSources.length})
      </label>
      <div className="border border-[#2D2A26] bg-black/40 max-h-[220px] overflow-y-auto">
        {(allSources || []).length === 0 ? (
          <div className="px-3 py-3 font-mono text-[10px] text-[#5a544a]">
            // No sources attached to this entity yet. Add via V4B Intake → promote.
          </div>
        ) : (
          <ul>
            {allSources.map((src) => {
              const isContextOnly = src.tag === "property_context_only";
              const isChosen = chosenSources.includes(src.id);
              const wouldViolate = isChosen && evidenceClass === "reported_allegation" && isContextOnly;
              return (
                <li
                  key={src.id}
                  className={
                    "px-3 py-2 border-b border-[#1f1d1a] last:border-b-0 flex items-start gap-2 " +
                    (wouldViolate ? "bg-[#D92D20]/[0.10]" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={isChosen}
                    onChange={() => toggleSource(src.id)}
                    data-testid={`admin-dossier-source-toggle-${src.id}`}
                    className="mt-1 accent-[#D92D20]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[11px] text-[#E4DFD3] truncate">
                      {src.title || src.url}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#7a7062]">
                      <span>{src.publisher || src.domain || "—"}</span>
                      {src.source_type && <span>· {src.source_type}</span>}
                      {isContextOnly && (
                        <span className="text-[#E0A526] border border-[#E0A526]/40 px-1.5 py-[1px]">
                          CONTEXT ONLY
                        </span>
                      )}
                    </div>
                    {wouldViolate && (
                      <div className="mt-1 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#D92D20]">
                        <AlertTriangle size={10} /> context-only source cannot back an allegation
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Action row */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => saveSection(null)}
          disabled={saving || !dirty}
          data-testid="admin-dossier-save-section"
          className={
            "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.35em] px-3 py-2 border " +
            (dirty && !saving
              ? "border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#5DD4B3]/10"
              : "border-[#454038] text-[#5a544a] cursor-not-allowed")
          }
        >
          {saving ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />}
          Save draft
        </button>
        <button
          onClick={() => saveSection("review")}
          disabled={saving}
          data-testid="admin-dossier-mark-review"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.35em] px-3 py-2 border border-[#E0A526] text-[#E0A526] hover:bg-[#E0A526]/10"
        >
          Mark for review
        </button>
        <button
          onClick={() => saveSection("approved")}
          disabled={saving || violatesAllegationRule}
          data-testid="admin-dossier-approve-section"
          className={
            "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.35em] px-3 py-2 border " +
            (violatesAllegationRule
              ? "border-[#454038] text-[#5a544a] cursor-not-allowed"
              : "border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20]/10")
          }
          title={violatesAllegationRule ? "Editorial rule blocks this approval" : ""}
        >
          <CheckCircle2 size={11} /> Approve
        </button>
        <div className="flex-1" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5a544a]">
          {section.body_md?.length || 0} chars
        </span>
      </div>

      {/* Section approval gate notice */}
      {(evidenceClass === "reported_allegation" || evidenceClass === "verified_fact") && chosenSources.length === 0 && (
        <div className="mt-3 inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/40 px-2 py-1">
          <AlertTriangle size={10} />
          Approval requires at least one source.
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span
      className="font-mono text-[9px] uppercase tracking-[0.3em] px-1.5 py-0.5 border"
      style={{ color: m.color, borderColor: `${m.color}66`, background: `${m.color}10` }}
    >
      {m.label}
    </span>
  );
};

const ClassPill = ({ ec }) => {
  const m = EVIDENCE_CLASS_META[ec] || EVIDENCE_CLASS_META.context_only;
  return (
    <span
      className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#7a7062]"
      title={m.label}
    >
      · {m.label}
    </span>
  );
};

export default AdminDossierEditor;
